import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowForward, Check, DoneAll } from '@mui/icons-material';

import {
  IAbweichung,
  IBereich,
  IBlattDraft,
  IBlattStand,
  IPatch,
  berechneAbweichungen,
} from './blattAbgleich';

/**
 * Der Auszug neben dem vorhandenen Blatt (#1336).
 *
 * Gezeigt wird nur, was sich unterscheidet — ein vollständiges Gegenüber wäre
 * bei einem Blatt mit dreißig Zeilen unlesbar, und die Aufgabe lautet nicht
 * „alles ansehen", sondern „entscheiden, was übernommen wird".
 *
 * Drei Sorten Zeilen:
 * * **neu** — steht nur im Auszug, wird hinzugefügt;
 * * **geändert** — steht in beiden, mit unterschiedlichem Inhalt;
 * * **fehlt** — steht nur in DiNo. Diese Zeilen haben **keine** Schaltfläche.
 *   Ein Recht zu löschen, weil eine Texterkennung es übersehen hat, ist der
 *   Ausgang, gegen den dieser ganze Weg gebaut ist. Wer wirklich löschen will,
 *   tut es in der jeweiligen Abteilung von Hand.
 *
 * Gespeichert wird hier nichts. Die Übernahme ändert den Stand im Editor; erst
 * dessen „Speichern" schreibt.
 */

const BEREICH_REIHENFOLGE: IBereich[] = [
  'Aufschrift',
  'Bestandsverzeichnis',
  'Abteilung I',
  'Abteilung II',
  'Abteilung III',
];

interface IProps {
  stand: IBlattStand;
  draft: IBlattDraft | null;
  onAnwenden: (patch: IPatch) => void;
  /** Alle übernehmbaren Abweichungen auf einmal. */
  onAlleAnwenden: (patchArray: IPatch[]) => void;
}

const ART_LABEL: Record<IAbweichung['Art'], string> = {
  neu: 'Neu im Auszug',
  feld: 'Geändert',
  fehlt: 'Nicht im Auszug',
};

const ART_FARBE: Record<IAbweichung['Art'], 'success' | 'warning' | 'default'> =
  {
    neu: 'success',
    feld: 'warning',
    fehlt: 'default',
  };

/** Lange Wortlaute in der Liste kürzen — der volle Text steht im Feld selbst. */
const kurz = (wert: string | null): string => {
  if (wert === null) {
    return '—';
  }

  return wert.length > 160 ? `${wert.slice(0, 160)} …` : wert;
};

const Zeile: React.FC<{
  abweichung: IAbweichung;
  onAnwenden: (_patch: IPatch) => void;
}> = ({ abweichung, onAnwenden }) => (
  <Stack
    direction={{ xs: 'column', md: 'row' }}
    spacing={1}
    alignItems={{ md: 'flex-start' }}
    sx={{ py: 1 }}
  >
    <Box sx={{ minWidth: 220, flexShrink: 0 }}>
      <Typography variant='body2'>{abweichung.Titel}</Typography>
      {abweichung.Feld !== null && (
        <Typography variant='caption' color='text.secondary'>
          {abweichung.Feld}
        </Typography>
      )}
    </Box>

    <Chip
      size='small'
      variant='outlined'
      color={ART_FARBE[abweichung.Art]}
      label={ART_LABEL[abweichung.Art]}
      sx={{ flexShrink: 0 }}
    />

    <Stack
      direction='row'
      spacing={1}
      alignItems='center'
      sx={{ flexGrow: 1, minWidth: 0 }}
    >
      {abweichung.Art !== 'neu' && (
        <Typography
          variant='body2'
          sx={{
            color:
              abweichung.Art === 'fehlt' ? 'text.primary' : 'text.secondary',
            wordBreak: 'break-word',
          }}
        >
          {kurz(abweichung.Alt)}
        </Typography>
      )}
      {abweichung.Art !== 'fehlt' && (
        <>
          {abweichung.Art === 'feld' && (
            <ArrowForward fontSize='inherit' color='disabled' />
          )}
          <Typography
            variant='body2'
            sx={{ fontWeight: 500, wordBreak: 'break-word' }}
          >
            {kurz(abweichung.Neu)}
          </Typography>
        </>
      )}
    </Stack>

    <Box sx={{ flexShrink: 0 }}>
      {abweichung.Patch !== null ? (
        <Button
          size='small'
          startIcon={<Check />}
          onClick={() => onAnwenden(abweichung.Patch as IPatch)}
        >
          Übernehmen
        </Button>
      ) : (
        <Typography variant='caption' color='text.secondary'>
          bleibt stehen
        </Typography>
      )}
    </Box>
  </Stack>
);

export const BlattAuszugAbgleich: React.FC<IProps> = ({
  stand,
  draft,
  onAnwenden,
  onAlleAnwenden,
}) => {
  // Aus dem **aktuellen** Stand berechnet: was übernommen wurde, ist keine
  // Abweichung mehr und verschwindet von selbst.
  const abweichungen = useMemo(
    () => berechneAbweichungen(stand, draft),
    [stand, draft]
  );

  const uebernehmbar = useMemo(
    () =>
      abweichungen.filter(a => a.Patch !== null).map(a => a.Patch as IPatch),
    [abweichungen]
  );

  const gruppen = useMemo(
    () =>
      BEREICH_REIHENFOLGE.map(bereich => ({
        bereich,
        zeilen: abweichungen.filter(a => a.Bereich === bereich),
      })).filter(gruppe => gruppe.zeilen.length > 0),
    [abweichungen]
  );

  if (draft === null) {
    return null;
  }

  if (abweichungen.length === 0) {
    return (
      <Alert severity='success'>
        Der Auszug stimmt mit dem gespeicherten Blatt überein. Es gibt nichts zu
        übernehmen.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Alert severity='info'>
        Das Blatt ist in DiNo bereits angelegt. Der Auszug <b>ergänzt</b> es:
        jede Abweichung wird einzeln übernommen, nichts wird überschrieben, und
        was der Auszug nicht enthält, bleibt unangetastet.
      </Alert>

      <Stack direction='row' spacing={1} alignItems='center'>
        <Typography variant='subtitle2'>
          {abweichungen.length === 1
            ? '1 Abweichung'
            : `${abweichungen.length} Abweichungen`}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {uebernehmbar.length > 0 && (
          <Button
            size='small'
            variant='outlined'
            startIcon={<DoneAll />}
            onClick={() => onAlleAnwenden(uebernehmbar)}
          >
            Alle {uebernehmbar.length} übernehmen
          </Button>
        )}
      </Stack>

      {gruppen.map(gruppe => (
        <Paper key={gruppe.bereich} variant='outlined' sx={{ p: 1.5 }}>
          <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
            {gruppe.bereich}
          </Typography>
          <Stack divider={<Divider flexItem />}>
            {gruppe.zeilen.map(abweichung => (
              <Zeile
                key={abweichung.id}
                abweichung={abweichung}
                onAnwenden={onAnwenden}
              />
            ))}
          </Stack>
        </Paper>
      ))}

      <Typography variant='caption' color='text.secondary'>
        Übernommene Abweichungen verschwinden aus dieser Liste. Gespeichert wird
        erst mit „Speichern" unten.
      </Typography>
    </Stack>
  );
};
